import { Box, Button, Icon, Text } from '@timelyapp/react';
import classNames from 'classnames';
import React, { memo, type SetStateAction, useCallback, useRef } from 'react';
import styles from './ReportExportModal.module.scss';
import type { ExportStatus } from './types';

interface ExportStatusDialogProps {
  status: ExportStatus;
  setStatus: (status: SetStateAction<ExportStatus>) => void;
  downloadUrl?: string;
  rowCount?: number;
  sheetCount?: number;
}

const getStatusMessage = (
  status: Omit<ExportStatus, 'READY'>,
  data: { rowCount?: number; sheetCount?: number } = {}
) => {
  if (status === 'LARGE_EXPORT') {
    return {
      title: 'Large export in progress...',
      message: `Preparing export of ${data.rowCount ?? 'N/A'} rows and ${
        data.sheetCount ?? 'N/A'
      } sheets. This might take a while.`,
      subMessage:
        "You can safely close this window, and a download link will be emailed as soon as it's ready. Remember, you can also return to the export menu to download the latest reports.",
      buttonLabel: 'Close and notify when ready',
    };
  } else if (status === 'SCHEDULE_SAVED') {
    return {
      title: 'Export schedule saved',
      message: 'Go back to delete or change any time.',
      buttonLabel: 'Done',
    };
  } else if (status === 'IN_PROGRESS_TAKING_A_WHILE') {
    return {
      title: 'Export in progress...',
      message:
        "The export takes a little longer, but we're on it. You can safely close this window.",
      subMessage:
        "Your report will be available to download from the export menu as soon as it's ready.",
      buttonLabel: 'Download',
    };
  } else {
    // IN_PROGRESS and IDLE case
    return {
      title: 'Download export',
      message: "Sit tight—we're getting your export ready!",
      subMessage:
        'Remember, you can also return to the export menu to download the latest reports.',
      buttonLabel: 'Download',
    };
  }
};

const ExportStatusDialog = memo(
  ({ status, setStatus, downloadUrl, rowCount, sheetCount }: ExportStatusDialogProps) => {
    const lastInProgressStatus = useRef<'IN_PROGRESS_TAKING_A_WHILE' | 'IN_PROGRESS' | undefined>();
    lastInProgressStatus.current =
      status === 'IN_PROGRESS_TAKING_A_WHILE' || status === 'IN_PROGRESS'
        ? status
        : lastInProgressStatus.current;
    const handleClose = useCallback(() => {
      setStatus('IDLE');
    }, [setStatus]);

    const handlePrimaryAction = useCallback(() => {
      if (status === 'READY') {
        window.open(downloadUrl, '_self');
      }
      handleClose();
    }, [downloadUrl, handleClose, status]);

    const { title, message, subMessage, buttonLabel } = getStatusMessage(
      status === 'READY' ? lastInProgressStatus.current! : status,
      {
        rowCount,
        sheetCount,
      }
    )!;

    const isOpen = status !== 'IDLE';
    const isInProgress = status === 'IN_PROGRESS' || status === 'IN_PROGRESS_TAKING_A_WHILE';

    return (
      // Handrolling this as the Dialog component lacks positioning / anchoring features (plus portal rendering complicates things)
      <>
        <div
          className={classNames(styles.statusDialogOverlay, {
            [styles.visible]: isOpen,
          })}
          onClick={handleClose}
        ></div>
        <div className={classNames(styles.statusDialog, { [styles.isOpen]: isOpen })}>
          <Box.Column gap="medium" style={{ position: 'relative' }}>
            <Text.Headline size="small">{title}</Text.Headline>
            <Text size="medium">{message}</Text>
            <Text size="small" color="subtle">
              {subMessage}
            </Text>
            <Button
              className={styles.primaryAction}
              onClick={handlePrimaryAction}
              disabled={isInProgress}
              isLoading={isInProgress}
              size="small"
              priority="xhigh"
            >
              {buttonLabel}
            </Button>
            {(status === 'READY' || isInProgress) && (
              <Button size="small" onClick={handleClose}>
                Close
              </Button>
            )}
            <Button className={styles.close} onClick={handleClose} size="xsmall" priority="xlow">
              <Icon name="XClose" type="solid" />
            </Button>
          </Box.Column>
        </div>
      </>
    );
  }
);

ExportStatusDialog.displayName = 'ExportStatusDialog';

export default ExportStatusDialog;
